import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import bemify from 'utils/bemify';
import PRODUCT_TYPES from 'utils/product-types';

// Hooks
import useAllergens from 'hooks/use-allergens';

// Components
import RestaurantMenuSidebar from 'components/restaurant-menu/sidebar';
import RestaurantMenuSidebarFilter from 'components/restaurant-menu/restaurant-menu-sidebar-filter';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const RestaurantMenuFiltersSidebar = ({
  className = null,
  onAllergensFilterUpdate = () => {},
  onClose = () => {},
  onPriceIntervalFilterUpdate = () => {},
  onProductTypeFilterUpdate = () => {},
  productTypes = null,
  productsAllergens = null,
  productsMaximumPrice = null,
  productsMinimumPrice = null,
  translations = null
}) => {
  const allAllergens = useAllergens(translations?.allergens || {});

  const allergens = allAllergens.filter(allergen => productsAllergens.includes(allergen.id));

  const productTypesItems = Array.isArray(productTypes) ?
    productTypes.map((productType) => {
      let label = translations.typeFilterAllLabel;

      if (productType === PRODUCT_TYPES.DISH) label = translations.typeFilterDishesLabel;
      if (productType === PRODUCT_TYPES.BEVERAGE) label = translations.typeFilterBeveragesLabel;

      return {
        label,
        value: productType
      };
    }) :
    [];

  const maximumRangeValue = Math.ceil(productsMaximumPrice);
  const minimumRangeValue = Math.floor(productsMinimumPrice);

  const [allergensFilters, setAllergensFilters] = useState(allergens);
  const [priceRangeValues, setPriceRangeValues] = useState({
    maximum: maximumRangeValue,
    minimum: minimumRangeValue
  });
  const [selectedProductType, setSelectedProductType] = useState(PRODUCT_TYPES.ALL);

  const dietFiltersAreCustom = useMemo(
    () => allergensFilters.some(filter => filter.checked),
    [allergensFilters]
  );

  const priceRangeValuesAreCustom = useMemo(
    () => priceRangeValues.maximum !== maximumRangeValue || priceRangeValues.minimum !== minimumRangeValue,
    [maximumRangeValue, minimumRangeValue, priceRangeValues]
  );

  const productTypeIsCustom = useMemo(
    () => selectedProductType !== PRODUCT_TYPES.ALL,
    [selectedProductType]
  );

  const handleAllergensFilterChange = (value) => {
    const updatedFilters = allergensFilters.map((item) => {
      if (item.label === value) {
        return {
          ...item,
          checked: !item.checked
        };
      }

      return item;
    });

    setAllergensFilters(updatedFilters);
  };

  const handleAllergensFilterResetButtonClick = () => {
    const clearedFilters = allergensFilters.map(item => ({
      ...item,
      checked: false
    }));

    setAllergensFilters(clearedFilters);
  };

  const handlePriceRangeFilterResetButtonClick = () => {
    const defaultValue = {
      maximum: maximumRangeValue,
      minimum: minimumRangeValue
    };

    setPriceRangeValues(defaultValue);
    onPriceIntervalFilterUpdate(defaultValue);
  };

  const handlePriceIntervalChange = (newValues) => {
    setPriceRangeValues(newValues);
    onPriceIntervalFilterUpdate(newValues);
  };

  const handleProductTypeChange = (newValue) => {
    setSelectedProductType(newValue);
    onProductTypeFilterUpdate(newValue);
  };

  const handleProductTypeResetButtonClick = () => {
    setSelectedProductType(PRODUCT_TYPES.ALL);
    onProductTypeFilterUpdate(PRODUCT_TYPES.ALL);
  };

  useEffect(() => {
    onAllergensFilterUpdate(allergensFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allergensFilters]);

  useEffect(() => {
    setAllergensFilters((currentAllergens) => {
      const newAllergens = currentAllergens.map((allergen) => {
        const allergenTranslation = translations.allergens[allergen.id];

        return {
          ...allergen,
          label: allergenTranslation || allergen.label
        };
      });

      return newAllergens;
    });
  }, [translations]);

  return (
    <RestaurantMenuSidebar
      className={className}
      translations={translations}
      onClose={onClose}
    >
      <div className={styles.restaurantMenuSidebarFilters}>
        <RestaurantMenuSidebarFilter
          maximumValue={maximumRangeValue}
          minimumValue={minimumRangeValue}
          rangeValues={priceRangeValues}
          showResetButton={priceRangeValuesAreCustom}
          title={translations.priceIntervalFilterSectionTitle}
          translations={translations}
          type="range"
          onChange={handlePriceIntervalChange}
          onResetButtonClick={handlePriceRangeFilterResetButtonClick}
        />
        <RestaurantMenuSidebarFilter
          items={allergensFilters}
          showResetButton={dietFiltersAreCustom}
          title={translations.dietFilterSectionTitle}
          translations={translations}
          type="checkbox"
          onChange={handleAllergensFilterChange}
          onResetButtonClick={handleAllergensFilterResetButtonClick}
        />
        {productTypesItems.length > 2 && (
          <RestaurantMenuSidebarFilter
            items={productTypesItems}
            selectedValue={selectedProductType}
            showResetButton={productTypeIsCustom}
            title={translations.productTypeFilterSectionTitle}
            translations={translations}
            type="radio"
            onChange={handleProductTypeChange}
            onResetButtonClick={handleProductTypeResetButtonClick}
          />
        )}
      </div>
    </RestaurantMenuSidebar>
  );
};

RestaurantMenuFiltersSidebar.propTypes = {
  className: PropTypes.string,
  onAllergensFilterUpdate: PropTypes.func,
  onClose: PropTypes.func,
  onPriceIntervalFilterUpdate: PropTypes.func,
  onProductTypeFilterUpdate: PropTypes.func,
  productTypes: PropTypes.arrayOf(PropTypes.string),
  productsAllergens: PropTypes.arrayOf(PropTypes.string),
  productsMaximumPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  productsMinimumPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  translations: PropTypes.shape({
    dietFilterSectionTitle: PropTypes.string,
    priceIntervalFilterSectionTitle: PropTypes.string,
    productTypeFilterSectionTitle: PropTypes.string,
    returnButtonLabel: PropTypes.string,
    typeFilterAllLabel: PropTypes.string,
    typeFilterBeveragesLabel: PropTypes.string,
    typeFilterDishesLabel: PropTypes.string
  })
};

export default RestaurantMenuFiltersSidebar;
